import React, { useContext, useMemo, useState } from 'react';
import striptags from 'striptags';
import { Event } from '@go-shared/types/event';
import { BareContentTemplateLegacy, ContentTemplateParams } from '@go-shared/types/contentTemplate';
import { Property } from '@go-shared/types/property';
import { EventEditor } from './EventEditor';

import './Blurb.scss';
import DraggableContentItem from '@go-client/components/DraggableContentItem';
import {
  BubbleAlert,
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
} from '@go-client/components/BubbleUI';
import Fuse from 'fuse.js';
import { firstOf } from '@go-shared/lib/functional';
import { DEFAULT_FUSE_SETTINGS } from '@go-client/lib/fuse';
import { GOSettings } from '@go-shared/types/info';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { safeIdCompare } from '@go-shared/lib/utils';
import { FeedArea } from '@go-shared/types/feedTemplate';

interface EventBlurbsProps {
  variant: 'navy' | 'velvet';
  events: Event[];
  property: Property;
}

export const EventBlurbs: React.FC<EventBlurbsProps> = ({ variant, events, property }) => {
  const { settings } = useServerInfo();
  const [selectedEvent, setSelectedEvent] = useState<Event | undefined>(undefined);
  const [searchValue, setSearchValue] = useState('');

  const processedEvents = useMemo(() => {
    return firstOf(
      events
        .sort((a, b) => {
          if (a.template.segment?._id === b.template.segment?._id) {
            return a.template.title.localeCompare(b.template.title);
          }

          if (a.template.segment == undefined) {
            return 1;
          }

          if (b.template.segment == undefined) {
            return -1;
          }

          return a.template.segment.name.localeCompare(b.template.segment.name);
        })
        .map(ev => eventToContentTemplate(ev, property, settings)),
      e => e._id
    );
  }, [events]);

  const fuse = useMemo(() => {
    return new Fuse(processedEvents, {
      ...DEFAULT_FUSE_SETTINGS,
      keys: ['name', 'content'],
    });
  }, [processedEvents]);

  const fuseSearchResults = useMemo(() => {
    if (!searchValue) return undefined;
    return fuse.search(searchValue).map(s => s.item);
  }, [fuse, searchValue]);

  return (
    <div className="EventBlurb">
      <BubbleInput
        block
        type="text"
        variant={variant}
        className={`p-4 sticky top-0 bg-${variant}-darkest`}
        value={searchValue}
        placeholder="Search..."
        onValue={setSearchValue}
      />
      <div className="grid grid-cols-1 p-4 pt-0 gap-1">
        {processedEvents.length < 1 && (
          <BubbleAlert variant="banana" className="w-full">
            There are no events on the canvas
          </BubbleAlert>
        )}
        {(fuseSearchResults || processedEvents).map(ev => (
          <DraggableContentItem key={ev._id} contentTemplate={ev} borderColor="lime" />
          // Disabled temporarily, this displays the event descriptions editor
          // <div
          //   className="event-blurb-wrapper relative flex justify-center flex-wrap w-full"
          //   key={ev._id}
          // >
          //   <div className="absolute edit-wrapper" style={{ top: 15, right: 30 }}>
          //     <TextButton onClick={() => setSelectedEvent(ev)} className="border-none">
          //       <InlineIcon name="edit" />
          //     </TextButton>
          //   </div>
          // </div>
        ))}
        <div className={`animated-wrapper shadow-md ${selectedEvent ? 'active' : ''}`}>
          {selectedEvent && (
            <EventEditor
              selectedEvent={selectedEvent}
              onClose={() => setSelectedEvent(undefined)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const eventToContentTemplate = (
  event: Event,
  property: Property,
  serverSettings: GOSettings
): BareContentTemplateLegacy => {
  const eventBlurbNoContainer = `<h2 class="EventBlurbHeader">${
    event.template.title
  }</h2><p class="EventBlurbContent">${striptags(event.template.description)}</p>`;
  const containerEventBlurb = `<div class="EventBlurbContainer">${eventBlurbNoContainer}</div>`;

  return {
    _id: event.template._id,
    content: serverSettings.USE_EVENT_BLURB_CONTAINER ? containerEventBlurb : eventBlurbNoContainer,
    name: event.template.title,
    property: property._id,
    params: {
      eventId: event.template._id,
    },
    notNative: event.template.notNative,
  };
};

interface EventBlurbSettingsProps {
  contentTemplate: BareContentTemplateLegacy;
  feedArea: FeedArea;
  onEventBlurbUpdated: (
    feedContent: FeedArea,
    templateId: string,
    eventTemplateInstance: number
  ) => void;
}
export function EventBlurbSettings(props: EventBlurbSettingsProps) {
  const context = useContext(FeedEditorCtx);

  const params = props.feedArea.content as ContentTemplateParams;
  const [eventTemplateInstance, setEventTemplateInstance] = useState(
    params.eventTemplateInstance ?? 0
  );

  let eventsInPlan: Event[] = [];

  if ((props.feedArea.content as any).params?.eventId) {
    // is event content blurb
    eventsInPlan = context.events.filter(e =>
      safeIdCompare(e.template._id, (props.feedArea.content as any).params.eventId)
    );
  }

  return (
    <div>
      <div className="p-4">
        {eventsInPlan.length > 0 ? (
          <BubbleFormGroup label="Instance" className="grid grid-cols-1 gap-1">
            {eventsInPlan.map((e, i) => (
              <div className="flex items-base relative" key={e._id}>
                <BubbleInput
                  type="checkbox"
                  className="mr-2"
                  checked={i === eventTemplateInstance}
                  onValue={chk => {
                    if (chk) setEventTemplateInstance(i);
                  }}
                  appearance="rounded"
                  inputClassName="stretched-link"
                />
                <span>{JSON.stringify(e.mergedVars?.replacements ?? {}, null, 2)}</span>
              </div>
            ))}
          </BubbleFormGroup>
        ) : (
          <BubbleAlert variant="ocean">No settings at this time</BubbleAlert>
        )}
      </div>
      <div className="p-4 sticky bottom-0 bg-white border-t flex items-center">
        <BubbleButton
          variant="navy"
          className="mr-auto"
          onClick={() =>
            props.onEventBlurbUpdated(
              props.feedArea,
              props.contentTemplate._id,
              eventTemplateInstance
            )
          }
          icon="rotate"
        >
          Sync Changes to Feed
        </BubbleButton>
      </div>
    </div>
  );
}
